import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SignalSlashIcon } from '@heroicons/react/24/outline';
import { useNetworkStatus } from '../hooks/useNetworkStatus';
import { useDateFormat } from '../hooks/useDateFormat';
import { CACHE_FRESH_EVENT, CACHE_SERVED_EVENT } from '../services/offlineCache';
import { useLocation } from 'react-router-dom';
import { isLiveRoute } from '../utils/liveRoute';

interface OfflineBannerProps {
  className?: string;
}

/**
 * Meldt dat het scherm de laatst bekende gegevens toont in plaats van verse.
 *
 * Zelfde contract als InstallPrompt: de component beslist zelf of hij zichtbaar is, zodat
 * hij één keer in App.tsx hangt en elk scherm dekt.
 *
 * Twee aanleidingen, want ze vallen niet samen: het toestel kan verbinding melden terwijl
 * de API onbereikbaar is (dan komt het signaal uit api.ts), en er kan cache getoond worden
 * op een moment dat de verbinding alweer terug is.
 */
export const OfflineBanner: React.FC<OfflineBannerProps> = ({ className = '' }) => {
  const { t } = useTranslation('common');
  const { formatDate } = useDateFormat();
  const isOnline = useNetworkStatus();
  const [cachedAt, setCachedAt] = useState<number | null>(null);

  useEffect(() => {
    const handleServed = (event: Event) => {
      const detail = (event as CustomEvent<{ cachedAt: number }>).detail;
      if (typeof detail?.cachedAt === 'number') setCachedAt(detail.cachedAt);
    };

    // Zodra er weer een vers antwoord binnenkomt is de melding niet meer waar.
    const handleFresh = () => setCachedAt(null);

    window.addEventListener(CACHE_SERVED_EVENT, handleServed);
    window.addEventListener(CACHE_FRESH_EVENT, handleFresh);

    return () => {
      window.removeEventListener(CACHE_SERVED_EVENT, handleServed);
      window.removeEventListener(CACHE_FRESH_EVENT, handleFresh);
    };
  }, []);

  const { pathname } = useLocation();

  const showingCache = cachedAt !== null;
  // Het live verslag heeft een donkere kop met een eigen verbindingsindicator; deze sticky
  // balk zou die kop omlaag duwen, precies waar de klok staat.
  const zichtbaar = !isLiveRoute(pathname) && (!isOnline || showingCache);

  // De balk dekt zelf de ruimte achter de statusbalk af, dus de witte strook die
  // CoachLayout daarvoor neerzet moet dan inklappen. Zonder dit staan ze onder elkaar
  // en zit er een witte band van een halve centimeter tussen de balk en de paginakop.
  useEffect(() => {
    document.body.classList.toggle('offlinebalk-zichtbaar', zichtbaar);
    return () => document.body.classList.remove('offlinebalk-zichtbaar');
  }, [zichtbaar]);

  if (!zichtbaar) {
    return null;
  }

  return (
    <div
      role="status"
      // pt via de inset zodat de balk onder de statusbalk uitkomt; op het web is de
      // inset 0 en verandert er niets. Via --safe-top en niet rechtstreeks via env(),
      // want op Android komt de waarde uit een geinjecteerde variabele - zie index.css.
      style={{ paddingTop: 'var(--safe-top)' }}
      // sticky en niet fixed: fixed neemt geen ruimte in, waardoor de balk over de
      // paginakop heen viel en de titel met de knoppen ernaast onbereikbaar werden.
      // Sticky duwt de pagina omlaag én blijft bij het scrollen in beeld staan, wat
      // voor een melding over verouderde gegevens ook de bedoeling is.
      className={`sticky top-0 inset-x-0 z-40 bg-vast-bruin text-white shadow-md ${className}`}
    >
      <div className="flex items-center gap-2 px-4 py-2 mx-auto max-w-3xl">
        <SignalSlashIcon className="w-5 h-5 shrink-0" aria-hidden="true" />
        <div className="text-sm leading-tight">
          <p className="font-semibold">
            {isOnline ? t('offline.cachedTitle') : t('offline.title')}
          </p>
          <p className="text-white/80">
            {showingCache
              ? t('offline.lastUpdated', {
                  time: formatDate(new Date(cachedAt), 'dayMonthTime'),
                })
              : t('offline.description')}
          </p>
        </div>
      </div>
    </div>
  );
};
